import { theme as antdTheme, type ThemeConfig } from "antd";

import { theme as baseTheme } from "design-system/config/theme";

import { SANDBOX_THEME_CSS_VAR_KEYS } from "./constants";
import { ThemeMode } from "./types";

export const lightTheme: ThemeConfig = {
  ...baseTheme,
  // `inherit: false` stops this theme from merging the parent (console)
  // light theme.
  inherit: false,
  cssVar: { prefix: "ant", key: SANDBOX_THEME_CSS_VAR_KEYS.light },
};

const darkColorTokens = {
  colorBgBase: "#000000",
  colorTextBase: "#ffffff",
  colorText: "rgba(255, 255, 255, 0.85)",
  colorTextSecondary: "rgba(255, 255, 255, 0.65)",
  colorTextTertiary: "rgba(255, 255, 255, 0.45)",
  colorTextQuaternary: "rgba(255, 255, 255, 0.25)",
  colorTextHeading: "rgba(255, 255, 255, 0.88)",
  colorTextLabel: "rgba(255, 255, 255, 0.65)",
  colorTextDescription: "rgba(255, 255, 255, 0.45)",
  colorTextDisabled: "rgba(255, 255, 255, 0.25)",
  colorTextPlaceholder: "rgba(255, 255, 255, 0.25)",
  colorIcon: "rgba(255, 255, 255, 0.65)",
  colorIconHover: "rgba(255, 255, 255, 0.88)",
  colorTextLightSolid: "#ffffff",
  colorBgContainer: "#141414",
  colorBgElevated: "#1f1f1f",
  colorBgLayout: "#181818",
  colorBgSpotlight: "#424242",
  colorBgMask: "rgba(0, 0, 0, 0.45)",
  colorBgContainerDisabled: "rgba(255, 255, 255, 0.04)",
  colorBgTextHover: "rgba(255, 255, 255, 0.08)",
  colorBgTextActive: "rgba(255, 255, 255, 0.18)",
  colorBorder: "#424242",
  colorBorderSecondary: "#303030",
  colorBorderBg: "#141414",
  colorFill: "rgba(255, 255, 255, 0.18)",
  colorFillSecondary: "rgba(255, 255, 255, 0.12)",
  colorFillTertiary: "rgba(255, 255, 255, 0.08)",
  colorFillQuaternary: "rgba(255, 255, 255, 0.04)",
  colorFillContent: "rgba(255, 255, 255, 0.12)",
  colorFillContentHover: "rgba(255, 255, 255, 0.18)",
  colorFillAlter: "rgba(255, 255, 255, 0.04)",
  colorFillAlterSolid: "#262626",
  colorFilledHandleBg: "#262626",
  colorSplit: "rgba(253, 253, 253, 0.12)",
  colorPrimary: "#2F54EB",
  colorPrimaryHover: "#597EF7",
  colorPrimaryActive: "#1D39C4",
  colorPrimaryBg: "#0D058F",
  colorPrimaryBgHover: "#2a2450",
  colorPrimaryBorder: "#3a3170",
  colorPrimaryBorderHover: "#4f44a0",
  colorPrimaryTextHover: "#8f84f5",
  colorPrimaryText: "#7c6cf0",
  colorPrimaryTextActive: "#a79cff",
  colorLink: "#7c6cf0",
  colorLinkHover: "#8f84f5",
  colorLinkActive: "#a79cff",
  colorInfo: "#7c6cf0",
  colorInfoHover: "#8f84f5",
  colorInfoActive: "#6a5fd0",
  colorInfoBg: "#1d1a3d",
  colorInfoBgHover: "#2a2450",
  colorInfoBorder: "#3a3170",
  colorInfoBorderHover: "#4f44a0",
  colorInfoTextHover: "#8f84f5",
  colorInfoText: "#7c6cf0",
  colorInfoTextActive: "#a79cff",
  colorSuccess: "#52c41a",
  colorSuccessBg: "#162312",
  colorSuccessBgHover: "#274916",
  colorSuccessBorder: "#3a611b",
  colorSuccessBorderHover: "#5f8a33",
  colorSuccessHover: "#73d13d",
  colorSuccessActive: "#3f8618",
  colorSuccessTextHover: "#73d13d",
  colorSuccessText: "#52c41a",
  colorSuccessTextActive: "#6abe39",
  colorWarning: "#faad14",
  colorWarningBg: "#2b2111",
  colorWarningBgHover: "#4e3a13",
  colorWarningBorder: "#6f4f17",
  colorWarningBorderHover: "#a26d1a",
  colorWarningHover: "#ffc53d",
  colorWarningActive: "#d48806",
  colorWarningTextHover: "#ffc53d",
  colorWarningText: "#faad14",
  colorWarningTextActive: "#ffd666",
  colorError: "#ff4d4f",
  colorErrorBg: "#2a1215",
  colorErrorBgHover: "#58181c",
  colorErrorBorder: "#a61d24",
  colorErrorBorderHover: "#d84a4f",
  colorErrorHover: "#ff7875",
  colorErrorActive: "#d32029",
  colorErrorTextHover: "#ff7875",
  colorErrorText: "#ff4d4f",
  colorErrorTextActive: "#f37370",
};

const darkLayoutTokens = {
  bodyBg: "#000000",
  footerBg: "#000000",
  headerBg: "#141414",
  lightSiderBg: "#141414",
  siderBg: "#000000",
  triggerBg: "#1f1f1f",
};

const darkTableTokens = {
  headerBg: "#1f1f1f",
  footerBg: "rgba(255, 255, 255, 0.04)",
  rowSelectedHoverBg: "rgba(124, 108, 240, 0.24)",
  headerFilterHoverBg: "rgba(124, 108, 240, 0.16)",
  headerBorderRadius: 4,
  headerSortActiveBg: "rgba(124, 108, 240, 0.2)",
  headerSortHoverBg: "rgba(124, 108, 240, 0.16)",
  cellwhiteBg: "#1f1f1f",
  cellgreyBg: "rgba(255, 255, 255, 0.04)",
};

const isColorValue = (value: unknown): boolean =>
  typeof value === "string" &&
  (value.startsWith("#") || value.startsWith("rgb") || value.startsWith("hsl"));

const toDarkComponentTokens = (tokens: Record<string, unknown> | undefined) =>
  Object.fromEntries(
    Object.entries(tokens ?? {}).filter(
      ([key, value]) => !key.startsWith("boxShadow") && !isColorValue(value),
    ),
  );

export const darkTheme: ThemeConfig = {
  ...lightTheme,
  inherit: false,
  cssVar: { prefix: "ant", key: SANDBOX_THEME_CSS_VAR_KEYS.dark },
  algorithm: antdTheme.darkAlgorithm,
  token: {
    ...lightTheme.token,
    ...darkColorTokens,
  },
  components: {
    ...Object.fromEntries(
      Object.entries(lightTheme.components ?? {}).map(([name, tokens]) => [
        name,
        toDarkComponentTokens({ ...(tokens as object) }),
      ]),
    ),
    Layout: {
      ...toDarkComponentTokens(lightTheme.components?.Layout),
      ...darkLayoutTokens,
    },
    Table: {
      ...toDarkComponentTokens(lightTheme.components?.Table),
      ...darkTableTokens,
    },
  },
};

export const sandboxThemes: Record<ThemeMode, ThemeConfig> = {
  light: lightTheme,
  dark: darkTheme,
};
